import {
    ChangeDetectionStrategy,
    Component,
    OnDestroy,
    afterRenderEffect,
    computed,
    effect,
    inject,
    input,
    signal,
    viewChild,
} from '@angular/core';

import { IDynamicElement } from '../_shared/models/dynamic/dynamic-element.model';
import { IHeroV3Slide } from '../_shared/models/hero-v3/hero-v3-slide.model';
import { IHeroV3 } from '../_shared/models/hero-v3/hero-v3.model';
import { BackgroundVariant } from '../_shared/models/hero/hero-variant.enum';
import { BreadcrumbService } from '../_shared/services/breadcrumb.service';
import { SliderComponent } from '../slider/slider.component';
import { HeroV3SlideComponent } from './hero-v3-slide.component';

@Component({
    selector: 'sirius-hero-v3',
    templateUrl: './hero-v3.component.html',
    styleUrls: ['./hero-v3.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [SliderComponent, HeroV3SlideComponent],
    host: {
        '[class.is-empty]': '!slides().length',
        '(mouseenter)': 'onMouseEnter()',
        '(mouseleave)': 'onMouseLeave()',
        '(focusin)': 'onFocusIn()',
        '(focusout)': 'onFocusOut()',
        '(pointerdown)': 'onPointerDown()',
    },
})
export class HeroV3Component implements IDynamicElement<IHeroV3>, OnDestroy {
    readonly config = input<IHeroV3 | undefined>(undefined);

    readonly slides = computed(() =>
        (this.config()?.items ?? []).map((slide: IHeroV3Slide) => ({
            ...slide,
            showVideoOnMobile: slide.showVideoOnMobile ?? false,
            contentAlignment: slide.contentAlignment ?? ('left' as const),
            variant: slide.variant ?? BackgroundVariant.DARK,
        }))
    );
    readonly autoRotate = computed<boolean>(() => this.config()?.autoRotate ?? false);
    readonly autoRotateInterval = computed<number>(() => this.config()?.autoRotateInterval ?? 7);
    readonly showArrows = computed<boolean>(() => this.config()?.showArrows ?? true);
    readonly showDots = computed<boolean>(() => this.config()?.showDots ?? true);

    readonly currentIndex = signal(0);

    private readonly _slider = viewChild<SliderComponent>(SliderComponent);
    private readonly _isPaused = signal(false);
    private readonly _breadcrumbService = inject(BreadcrumbService);

    constructor() {
        afterRenderEffect((onCleanup) => {
            if (!this.autoRotate() || this._isPaused() || this.slides().length <= 1) return;

            const id = setInterval(() => this._slider()?.scrollNext(), this.autoRotateInterval() * 1000);
            onCleanup(() => clearInterval(id));
        });

        effect(() => {
            const slides = this.slides();
            if (!slides.length) return;

            const activeSlide = slides[this.currentIndex()] ?? slides[0];
            this._breadcrumbService.setVariant(activeSlide.variant ?? BackgroundVariant.DARK);
        });
    }

    ngOnDestroy(): void {
        this._breadcrumbService.reset();
    }

    onSlideChange(index: number): void {
        this.currentIndex.set(index);
    }

    onPointerDown(): void {
        this._isPaused.set(true);
    }

    onMouseEnter(): void {
        this._isPaused.set(true);
    }

    onMouseLeave(): void {
        this._isPaused.set(false);
    }

    onFocusIn(): void {
        this._isPaused.set(true);
    }

    onFocusOut(): void {
        this._isPaused.set(false);
    }
}
